import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, MapPin, MousePointerClick, Paperclip, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { icon: Globe,            key: 'step1' },
  { icon: MapPin,           key: 'step2' },
  { icon: MousePointerClick,key: 'step3' },
  { icon: Paperclip,        key: 'step4' },
];

export const HowToApply = () => {
  const { t } = useTranslation();
  const [videoUrl, setVideoUrl] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/public/howto-video-url`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setVideoUrl(d.url))
      .catch(() => {});
  }, []);

  return (
    <section
      aria-labelledby="how-to-apply-title"
      className="border-t border-slate-100 bg-slate-50"
      data-testid="how-to-apply-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <header className="mb-10 sm:mb-12">
          <p className="text-xs font-semibold tracking-[0.10em] uppercase text-blue-700 mb-3">
            {t('howToApply.eyebrow')}
          </p>
          <h2
            id="how-to-apply-title"
            className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900"
          >
            {t('howToApply.title')}
          </h2>
        </header>

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
          {/* Passos */}
          <div className="flex-1 space-y-6">
            {STEPS.map(({ icon: Icon, key }, i) => (
              <div key={key} className="flex items-start gap-4" data-testid={`howto-${key}`}>
                <div className="flex-none w-10 h-10 rounded-full bg-blue-700 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                  {i + 1}
                </div>
                <div className="pt-1.5">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Icon className="h-4 w-4 text-blue-600" aria-hidden="true" />
                    <p className="font-semibold text-slate-900 text-[15px]">{t(`howToApply.${key}`)}</p>
                  </div>
                </div>
              </div>
            ))}

            {/* Aviso importante */}
            <div
              className="mt-8 flex gap-3 rounded-xl border border-amber-300 bg-amber-50 px-5 py-4"
              role="note"
              data-testid="howto-warning"
            >
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-none mt-0.5" aria-hidden="true" />
              <p className="text-sm text-amber-900 leading-[1.6]">
                <strong className="font-semibold">{t('howToApply.warningLabel')}</strong>{' '}
                {t('howToApply.warningText')}
              </p>
            </div>
          </div>

          {/* Slot do vídeo vertical */}
          <div className="w-full lg:w-[280px] xl:w-[300px] flex-none" data-testid="howto-video-slot">
            <div className="relative w-full" style={{ aspectRatio: '9/16' }}>
              {videoUrl ? (
                <video
                  src={videoUrl}
                  className="w-full h-full object-cover rounded-2xl shadow-lg"
                  controls
                  playsInline
                  preload="metadata"
                  aria-label={t('howToApply.videoAria')}
                />
              ) : (
                <div className="w-full h-full rounded-2xl border-2 border-dashed border-slate-300 bg-white flex flex-col items-center justify-center gap-3 text-slate-400">
                  <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                  </svg>
                  <p className="text-xs text-center px-4">{t('howToApply.videoPlaceholder')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
